import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { EditorialHeading } from "./Reveal";

const IMAGES = [
  "https://images.unsplash.com/photo-1685537711146-d6c29e41eeb6?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  "https://images.unsplash.com/photo-1758907468817-62c2fcba1665?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  "https://images.unsplash.com/photo-1638885930125-85350348d266?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  "https://images.unsplash.com/photo-1767680148632-137c54313c35?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  "https://images.unsplash.com/photo-1650753174235-7dae04cefd0d?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  "https://images.unsplash.com/photo-1781344334903-f33d8b76e292?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
];

export const Services = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const trackRef = useRef(null);
  const [endX, setEndX] = useState(-1400);

  useEffect(() => {
    const measure = () => {
      if (!trackRef.current) return;
      const extra = trackRef.current.scrollWidth - window.innerWidth;
      setEndX(-(extra + 40));
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [t]);

  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
  const x = useTransform(scrollYProgress, [0.05, 1], [0, endX]);
  const progressW = useTransform(scrollYProgress, [0.05, 1], ["0%", "100%"]);

  return (
    <section id="services" data-testid="services-section" ref={ref} className="relative h-[320vh]">
      <div className="sticky top-0 h-screen overflow-hidden flex flex-col justify-center gap-8 sm:gap-10">
        <div className="max-w-7xl mx-auto w-full px-5 sm:px-8 flex items-end justify-between gap-6">
          <EditorialHeading kicker={t.services.kicker} lines={t.services.lines} size="text-3xl sm:text-4xl lg:text-5xl" />
          <span className="hidden sm:block font-tech text-[11px] tracking-[0.3em] uppercase text-neutral-400 pb-2 shrink-0">
            01 — 0{t.services.items.length}
          </span>
        </div>

        <motion.div ref={trackRef} style={{ x }} className="flex gap-5 sm:gap-8 px-5 sm:px-[6vw] w-max">
          {t.services.items.map((s, i) => (
            <div
              key={i}
              data-testid={`service-card-${i}`}
              className="group relative w-[78vw] sm:w-[42vw] lg:w-[30vw] h-[48vh] sm:h-[54vh] shrink-0 overflow-hidden rounded-xl border border-[#e8e5e0] bg-neutral-900"
            >
              <img
                src={IMAGES[i]}
                alt={s.title}
                className="absolute inset-0 w-full h-full object-cover opacity-90 transition-transform duration-[900ms] ease-out group-hover:scale-[1.07]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/15 to-black/10" />
              <span className="absolute top-5 left-6 font-display italic text-5xl sm:text-6xl text-white/70">0{i + 1}</span>
              <ArrowUpRight
                size={22}
                className="absolute top-6 right-6 text-white/60 transition-all duration-300 group-hover:text-[#ff6b00] group-hover:translate-x-1 group-hover:-translate-y-1"
              />
              <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-7">
                <h3 className="font-display text-2xl sm:text-3xl text-white leading-tight">{s.title}</h3>
                <p className="mt-2.5 text-xs sm:text-sm text-white/65 leading-relaxed max-w-sm">{s.desc}</p>
              </div>
            </div>
          ))}
        </motion.div>

        <div className="max-w-7xl mx-auto w-full px-5 sm:px-8">
          <div className="h-[2px] bg-[#e8e5e0] rounded-full overflow-hidden">
            <motion.span style={{ width: progressW }} className="block h-full bg-gradient-to-r from-[#ff6b00] to-[#ffd700]" />
          </div>
        </div>
      </div>
    </section>
  );
};
